import React from 'react';
import { usePdfViewer } from '@/hooks/viewers/usePdfViewer.ts';
import PdfCompactViewer from './variants/PdfCompactViewer';
import PdfFullViewer from './variants/PdfFullViewer';
import ViewerFullScreenDialog from './ViewerFullScreenDialog.tsx';
import '@/utils/pdfConfig';

interface PdfViewerProps {
	fileUrl: string;
	title?: string;
	mode?: 'full' | 'compact';
	maxWidth?: number;
	showExpandButton?: boolean;
	showExternalLink?: boolean;
	disableHoverEffects?: boolean;
	isHighlighted?: boolean;
	showPageControls?: boolean;
	/** Page to open the preview at (1-based) — e.g. a cited source's page. */
	initialPage?: number;
	showPreviewHeader?: boolean;
	fullscreenPageRequest?: { page: number; requestId: number } | null;
	unstyledCompact?: boolean;
	onPreviewAspectChange?: (aspect: number) => void;
}

/**
 * PDF viewer component with navigation controls for multi-page documents
 *
 * Supports both full viewer mode and compact preview mode for embedding in chat responses.
 * The compact mode implements Google Drive-style PDF previews with cropped aspect ratio
 * and hover effects for better visual presentation in constrained spaces.
 */
const PdfViewer: React.FC<PdfViewerProps> = (props) => {
	const {
		title,
		fileUrl,
		mode = 'full',
		maxWidth = 300,
		showExternalLink = false,
		disableHoverEffects = false,
		isHighlighted = false,
		showExpandButton = true,
		showPageControls = true,
		initialPage,
		showPreviewHeader,
		fullscreenPageRequest,
		unstyledCompact,
		onPreviewAspectChange,
	} = props;

	// Local state for expanded dialog
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [showAllPagesFullscreen, setShowAllPagesFullscreen] = React.useState(true);

	const onClose = () => setIsExpanded(false);
	const requestedInitialPage = fullscreenPageRequest?.page ?? initialPage;

	React.useEffect(() => {
		if (!fullscreenPageRequest) return;
		setShowAllPagesFullscreen(true);
		setIsExpanded(true);
	}, [fullscreenPageRequest?.requestId]);

	const openDocumentFullscreen = () => {
		setShowAllPagesFullscreen(true);
		setIsExpanded(true);
	};

	const {
		numPages,
		pageNumber,
		loading,
		pdfOptions,
		onDocumentLoadSuccess,
		onDocumentLoadError,
		goToPrevPage,
		goToNextPage,
		handlePageNumberChange,
		openInNewTab,
		setPageChangeReason,
	} = usePdfViewer({ fileUrl, isOpen: isExpanded, initialPage: requestedInitialPage });

	function renderPdfFullScreenDialog() {
		return (
			<ViewerFullScreenDialog
				title={title ?? ''}
				isOpen={isExpanded}
				onClose={onClose}
				pageNumber={pageNumber}
				numPages={numPages}
				loading={loading}
				pdfOptions={pdfOptions}
				onDocumentLoadSuccess={onDocumentLoadSuccess}
				onDocumentLoadError={onDocumentLoadError}
				goToPrevPage={goToPrevPage}
				goToNextPage={goToNextPage}
				onPageChange={handlePageNumberChange}
				setPageChangeReason={setPageChangeReason}
				showAllPages={showAllPagesFullscreen}
			/>
		);
	}

	if (mode === 'compact') {
		return (
			<>
				<PdfCompactViewer
					title={title}
					maxWidth={maxWidth}
					showExternalLink={showExternalLink}
					numPages={numPages}
					pageNumber={pageNumber}
					loading={loading}
					pdfOptions={pdfOptions}
					onDocumentLoadSuccess={onDocumentLoadSuccess}
					onDocumentLoadError={onDocumentLoadError}
					goToPrevPage={goToPrevPage}
					goToNextPage={goToNextPage}
					openInNewTab={openInNewTab}
					onExpand={openDocumentFullscreen}
					disableHoverEffects={disableHoverEffects}
					isHighlighted={isHighlighted}
					showExpandButton={showExpandButton}
					showPageControls={showPageControls}
					showPreviewHeader={showPreviewHeader}
					unstyledCompact={unstyledCompact}
					onPreviewAspectChange={onPreviewAspectChange}
				/>
				{isExpanded && renderPdfFullScreenDialog()}
			</>
		);
	}

	// Full mode (default)
	return (
		<>
			<PdfFullViewer
				numPages={numPages}
				pageNumber={pageNumber}
				loading={loading}
				pdfOptions={pdfOptions}
				onDocumentLoadSuccess={onDocumentLoadSuccess}
				onDocumentLoadError={onDocumentLoadError}
				goToPrevPage={goToPrevPage}
				goToNextPage={goToNextPage}
				onExpand={openDocumentFullscreen}
				showPageControls={showPageControls}
			/>
			{isExpanded && renderPdfFullScreenDialog()}
		</>
	);
};

export default PdfViewer;
